import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { GlobalNavigation } from '../components/common/GlobalNavigation';
import MasterData from './MasterData';
import { X, Building } from 'lucide-react';
import { Supplier } from '../types/contract.types';

interface ContractTypeCategory {
  id: string;
  name: string;
  types: ContractTypeOption[];
}

interface ContractTypeOption {
  id: string;
  name: string;
  category: string;
}

export const MasterDataPage: React.FC = () => {
  const { user } = useAuth();
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);
  
  // Suppliers state
  const [suppliers, setSuppliers] = useState<Supplier[]>([
    { 
      id: 1, 
      companyName: 'ABC Corp', 
      taxId: 'TAX001',
      address: {
        street: '123 Main St',
        city: 'New York',
        state: 'NY',
        zipCode: '10001',
        country: 'United States'
      },
      contactPersons: [{
        name: 'John Doe',
        role: 'Primary Contact',
        email: 'john@abc.com',
        phone: '123-456-7890',
        isPrimary: true
      }],
      category: 'Technology',
      status: 'active'
    },
    { 
      id: 2, 
      companyName: 'XYZ Ltd', 
      taxId: 'TAX002',
      address: {
        street: '456 Oak Ave',
        city: 'Los Angeles',
        state: 'CA',
        zipCode: '90210',
        country: 'United States'
      },
      contactPersons: [{
        name: 'Jane Smith',
        role: 'Primary Contact',
        email: 'jane@xyz.com',
        phone: '098-765-4321',
        isPrimary: true
      }],
      category: 'Services',
      status: 'active'
    }
  ]);

  const [supplierForm, setSupplierForm] = useState<Supplier>({
    id: 0,
    companyName: '',
    taxId: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'United States'
    },
    contactPersons: [{
      name: '',
      role: 'Primary Contact',
      email: '',
      phone: '',
      isPrimary: true
    }],
    category: 'Technology',
    status: 'active'
  });

  // Contract types state
  const [contractTypes, setContractTypes] = useState<ContractTypeCategory[]>([
    {
      id: 'foundational',
      name: 'Foundational Agreements',
      types: [
        { id: 'nda', name: 'Non-Disclosure Agreement (NDA)', category: 'foundational' },
        { id: 'mou', name: 'Memorandum of Understanding (MOU)', category: 'foundational' },
        { id: 'loi', name: 'Letter of Intent (LOI)', category: 'foundational' }
      ]
    },
    {
      id: 'employment',
      name: 'Employment & Contractor Agreements',
      types: [
        { id: 'employment', name: 'Employment Contract', category: 'employment' },
        { id: 'contractor', name: 'Independent Contractor Agreement', category: 'employment' },
        { id: 'non-compete', name: 'Non-Compete Agreement', category: 'employment' },
        { id: 'non-solicitation', name: 'Non-Solicitation Agreement', category: 'employment' }
      ]
    },
    {
      id: 'business',
      name: 'Business Relationship Agreements',
      types: [
        { id: 'partnership', name: 'Partnership Agreement', category: 'business' },
        { id: 'joint-venture', name: 'Joint Venture Agreement', category: 'business' },
        { id: 'shareholder', name: 'Shareholder Agreement', category: 'business' },
        { id: 'operating', name: 'Operating Agreement', category: 'business' }
      ]
    },
    {
      id: 'commercial',
      name: 'Commercial Agreements',
      types: [
        { id: 'purchase', name: 'Purchase Agreement / Sales Contract', category: 'commercial' },
        { id: 'sla', name: 'Service Level Agreement (SLA)', category: 'commercial' },
        { id: 'msa', name: 'Master Service Agreement (MSA)', category: 'commercial' },
        { id: 'distribution', name: 'Distribution Agreement', category: 'commercial' },
        { id: 'franchise', name: 'Franchise Agreement', category: 'commercial' },
        { id: 'licensing', name: 'Licensing Agreement', category: 'commercial' }
      ]
    },
    {
      id: 'financial',
      name: 'Financial & Investment Agreements',
      types: [
        { id: 'loan', name: 'Loan Agreement', category: 'financial' },
        { id: 'investment', name: 'Investment Agreement', category: 'financial' },
        { id: 'lease', name: 'Lease Agreement', category: 'financial' },
        { id: 'guarantee', name: 'Guarantee Agreement', category: 'financial' }
      ]
    },
    {
      id: 'legal',
      name: 'Legal & Compliance Agreements',
      types: [
        { id: 'settlement', name: 'Settlement Agreement', category: 'legal' },
        { id: 'indemnity', name: 'Indemnity Agreement', category: 'legal' },
        { id: 'tos', name: 'Terms of Service / Terms and Conditions', category: 'legal' },
        { id: 'privacy', name: 'Privacy Policy', category: 'legal' }
      ]
    }
  ]);

  // Check if user has permission to access Master Data
  if (!user || user.role !== 'Admin') {
    return <Navigate to="/" replace />;
  }

  const handleAddSupplier = () => {
    setEditingSupplier(null);
    setSupplierForm({
      id: 0,
      companyName: '',
      taxId: '',
      address: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'United States'
      },
      contactPersons: [{
        name: '',
        role: 'Primary Contact',
        email: '',
        phone: '',
        isPrimary: true
      }],
      category: 'Technology',
      status: 'active'
    });
    setShowSupplierModal(true);
  };

  const handleEditSupplier = (supplier: Supplier) => {
    setEditingSupplier(supplier);
    setSupplierForm(supplier);
    setShowSupplierModal(true);
  };

  const handleDeleteSupplier = (id: number) => {
    if (window.confirm('Are you sure you want to delete this supplier?')) {
      setSuppliers(suppliers.filter(s => s.id !== id));
    }
  };

  const handleSaveSupplier = () => {
    if (editingSupplier) {
      setSuppliers(suppliers.map(s => s.id === editingSupplier.id ? supplierForm : s));
    } else {
      const newSupplier = {
        ...supplierForm,
        id: Math.max(...suppliers.map(s => typeof s.id === 'string' ? parseInt(s.id) || 0 : s.id), 0) + 1
      };
      setSuppliers([...suppliers, newSupplier]);
    }
    setShowSupplierModal(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <GlobalNavigation 
        title="Master Data Management"
        breadcrumbs={[
          { label: 'Home', path: '/' },
          { label: 'Master Data' }
        ]}
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <MasterData 
          suppliers={suppliers}
          contracts={[]}
          contractTypes={contractTypes}
          onAddSupplier={handleAddSupplier}
          onEditSupplier={handleEditSupplier}
          onDeleteSupplier={handleDeleteSupplier}
          onUpdateContractTypes={setContractTypes}
        />
      </div>

      {/* Supplier Modal */}
      {showSupplierModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Building className="w-5 h-5" />
                  {editingSupplier ? 'Edit Supplier' : 'Add New Supplier'}
                </h2>
                <button
                  onClick={() => setShowSupplierModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <form className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Name
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={supplierForm.companyName}
                    onChange={(e) => setSupplierForm({...supplierForm, companyName: e.target.value})}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Person
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={supplierForm.contactPersons[0]?.name || ''}
                    onChange={(e) => setSupplierForm({
                      ...supplierForm, 
                      contactPersons: [{
                        ...supplierForm.contactPersons[0],
                        name: e.target.value
                      }]
                    })}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={supplierForm.contactPersons[0]?.email || ''}
                    onChange={(e) => setSupplierForm({
                      ...supplierForm, 
                      contactPersons: [{
                        ...supplierForm.contactPersons[0],
                        email: e.target.value
                      }]
                    })}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={supplierForm.contactPersons[0]?.phone || ''}
                    onChange={(e) => setSupplierForm({
                      ...supplierForm, 
                      contactPersons: [{
                        ...supplierForm.contactPersons[0],
                        phone: e.target.value
                      }]
                    })}
                  />
                </div>
              </form>
            </div>
            
            <div className="p-6 border-t bg-gray-50 flex justify-end gap-3">
              <button
                onClick={() => setShowSupplierModal(false)}
                className="px-4 py-2 text-gray-700 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveSupplier}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                {editingSupplier ? 'Update' : 'Create'} Supplier
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}; 